import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import React, { useEffect, useState } from "react";

import { RichNavigationBannerContentAssembly, templates } from "@reactivated";

import {
    StreamField,
    StreamFieldBlockMap,
} from "@thelabnyc/thelabui/src/components/StreamField";
import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { useRichNavViewport } from "../../hooks/useRichNavViewport";
import { BannerBlock } from "../BannerBlock";
import { RichNavigationCollectionContainerBlock } from "../RichNavigationCollectionContainer";
import { RichNavigationLink } from "../RichNavigationLink";
import {
    RichNavigationLinkCollection,
    ValueProps,
} from "../RichNavigationLinkCollection";

import styles from "./index.module.scss";

export type LinkCollection = { id: number; isOpen: boolean };

export const bannerContentAssemblyComponents: StreamFieldBlockMap<RichNavigationBannerContentAssembly> =
    {
        banner: BannerBlock,
    };

type IRichNavigationProps = {
    openMobileContentDefaultState?: boolean;
    enableValuePropsOnMobile?: boolean;
    enableMobileAccordion?: boolean;
    /**
     * When true on mobile, render a plain div/ul/li shell instead of Radix
     * NavigationMenu.Root so nested <nav> landmarks are not emitted inside the
     * mobile menu panel (A360 #299).
     */
    suppressNavLandmark?: boolean;
    /**
     * Dev-only: keep this top-level menu open (Radix value). Use in Storybook or
     * with `?debugNavOpen=pillows` on the URL.
     */
    pinnedOpenMenuItemId?: string;
};

/**
 * Mobile plain shell for `suppressNavLandmark`. Accepts `value` /
 * `onValueChange` so it can share a call site with `NavigationMenu.Root`, but
 * those props are intentionally ignored — Radix open-state / overlay tracking
 * does not apply on this path.
 */
const PlainMenuRoot = ({
    className,
    children,
}: React.PropsWithChildren<{
    className?: string;
    value?: string;
    onValueChange?: (value: string) => void;
}>): React.JSX.Element => <div className={className}>{children}</div>;

const PlainMenuList = ({
    className,
    "data-orientation": dataOrientation,
    children,
}: React.PropsWithChildren<{
    "className"?: string;
    "data-orientation"?: string;
}>): React.JSX.Element => (
    <ul className={className} data-orientation={dataOrientation}>
        {children}
    </ul>
);

const PlainMenuItem = ({
    className,
    children,
}: React.PropsWithChildren<{
    className?: string;
    value?: string;
}>): React.JSX.Element => <li className={className}>{children}</li>;

const getNavItemValue = (
    block: { id?: string; type: string },
    index: number,
): string => {
    if (block.id) {
        return block.id;
    }
    return `${block.type}-${index}`;
};
type RichNavigationProps = IRichNavigationProps &
    templates.RichNavigationTemplate["rich_navigation"];

type NavContentBlock = NonNullable<
    RichNavigationProps["content"]
>["value"][number];

const resolvePinnedMenuItemId = (
    pin: string,
    blocks: NavContentBlock[],
): string | undefined => {
    const normalizedPin = pin.trim().toLowerCase();
    for (let i = 0; i < blocks.length; i += 1) {
        const block = blocks[i];
        if (getNavItemValue(block, i).toLowerCase() === normalizedPin) {
            return getNavItemValue(block, i);
        }
        let blockName: string | null = null;
        if (block.type === "rich_nav_collection_container") {
            const container = block.value;
            blockName =
                container.title?.trim() ||
                container.collections?.[0]?.name ||
                null;
        } else if (block.type === "rich_nav_link_collection") {
            blockName = block.value.name;
        } else if (block.type === "link") {
            blockName = block.value.title;
        }
        if (blockName && blockName.trim().toLowerCase() === normalizedPin) {
            return getNavItemValue(block, i);
        }
    }
    const index = Number(pin);
    if (!Number.isNaN(index) && blocks[index]) {
        return getNavItemValue(blocks[index], index);
    }
    return undefined;
};

/**
 * The desktop/mobile split (Radix vs accordion) is driven by the shared
 * `belowMobile` breakpoint (`$mobile: 768px`). If a host renders this in a
 * panel that is always mobile but shows at a boundary wider than `$mobile`
 * (e.g. the Tempur-Pedic header's inclusive `max-width: 768px`), wrap it in
 * `RichNavMobileLayoutProvider` (from `hooks/useRichNavViewport`) to force the
 * subtree into mobile layout; otherwise it silently follows the shared 767px
 * breakpoint.
 */
export const RichNavigation = (
    props: RichNavigationProps,
): React.JSX.Element | null => {
    const viewport = useRichNavViewport();
    const mobileA11yIdPrefix = React.useId();
    const [pinnedFromUrl, setPinnedFromUrl] = useState<string | undefined>(
        undefined,
    );
    const pinnedOpenMenuItemId = props.pinnedOpenMenuItemId ?? pinnedFromUrl;
    const [menuValue, setMenuValue] = useState("");
    const isOpen = Boolean(pinnedOpenMenuItemId ?? menuValue);

    useEffect(() => {
        if (!props.content?.value.length) {
            return;
        }
        const win = getWindow();
        if (!win) {
            return;
        }
        const pin = new URLSearchParams(win.location.search).get(
            "debugNavOpen",
        );
        if (!pin) {
            return;
        }
        const resolved = resolvePinnedMenuItemId(pin, props.content.value);
        if (resolved) {
            setPinnedFromUrl(resolved);
        }
    }, [props.content?.value]);

    // Create an Array for open/close states of link collections on mobile
    // Use openMobileContentDefaultState prop to set the initial state of the menus
    // Otherwise initially open the first collection
    const linkCollections: LinkCollection[] = props.content
        ? props.content.value.map((_, i: number) => ({
              id: i,
              isOpen: props.openMobileContentDefaultState ?? i === 0,
          }))
        : [];
    const [openMobileContent, setOpenMobileContent] =
        useState<LinkCollection[]>(linkCollections);
    const usePlainMenuShell = Boolean(
        props.suppressNavLandmark && viewport.belowMobile,
    );
    const MenuRoot = usePlainMenuShell ? PlainMenuRoot : NavigationMenu.Root;
    const MenuList = usePlainMenuShell ? PlainMenuList : NavigationMenu.List;
    const MenuItem = usePlainMenuShell ? PlainMenuItem : NavigationMenu.Item;

    return (
        <MenuRoot
            className={styles.root}
            value={pinnedOpenMenuItemId ?? menuValue}
            onValueChange={(val: string) => {
                if (pinnedOpenMenuItemId) {
                    return;
                }
                setMenuValue(val);
            }}
        >
            <MenuList
                data-orientation={
                    viewport.belowMobile ? "vertical" : "horizontal"
                }
                className={styles.list}
            >
                {props.content &&
                    props.content.value.map((block, i: number) => {
                        switch (block.type) {
                            case "rich_nav_link_collection":
                                return (
                                    <MenuItem
                                        key={i}
                                        className={styles.item}
                                        value={getNavItemValue(block, i)}
                                    >
                                        <RichNavigationLinkCollection
                                            collection={block}
                                            banner_content={
                                                props.banner_content &&
                                                !viewport.belowMobile
                                                    ? props.banner_content.value
                                                    : null
                                            }
                                            value_props={props.value_props}
                                            openMobileContent={
                                                openMobileContent
                                            }
                                            setOpenMobileContent={
                                                setOpenMobileContent
                                            }
                                            mobileContentIndex={i}
                                            enableMobileAccordion={
                                                props.enableMobileAccordion
                                            }
                                            mobileA11yIdPrefix={
                                                mobileA11yIdPrefix
                                            }
                                        />
                                    </MenuItem>
                                );
                            case "rich_nav_collection_container":
                                return (
                                    <MenuItem
                                        key={i}
                                        className={styles.item}
                                        value={getNavItemValue(block, i)}
                                    >
                                        <RichNavigationCollectionContainerBlock
                                            value={block.value}
                                            mobileContentIndex={i}
                                            mobileA11yIdPrefix={
                                                mobileA11yIdPrefix
                                            }
                                        />
                                    </MenuItem>
                                );
                            case "link":
                                return (
                                    <MenuItem
                                        key={i}
                                        className={styles.item}
                                        value={getNavItemValue(block, i)}
                                    >
                                        <RichNavigationLink {...block.value} />
                                    </MenuItem>
                                );
                            default:
                                return <></>;
                        }
                    })}
            </MenuList>
            {props.enableValuePropsOnMobile && viewport.belowMobile && (
                <ValueProps
                    value_props={props.value_props}
                    enableValuePropsOnMobile={true}
                />
            )}
            {props.banner_content && viewport.belowMobile && (
                <StreamField
                    components={bannerContentAssemblyComponents}
                    value={props.banner_content.value}
                />
            )}
            {isOpen && <div className={styles.overlay}></div>}
        </MenuRoot>
    );
};
